import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { CSSProperties, FC, ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { MODAL_ANIMATION_DURATION } from '@/shared/config';
import { useMedia } from '@/shared/hooks';

import { Button, ButtonProps } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Portal } from '../Portal/Portal';

import {
  defaultModalAnimationVariants,
  mobileModalAnimationVariants,
} from './Modal.config';
import styles from './Modal.module.scss';

export type ModalProps = {
  isOpen: boolean;
  onCancel?: () => void;
  onOk?: () => void;
  testID?: string;
  className?: string;
  containerClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  title?: string | JSX.Element;
  okButtonText?: string | JSX.Element;
  cancelButtonText?: string | JSX.Element;
  shouldRenderCloseIconButton?: boolean;
  shouldRenderCancelButton?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  children?: ReactNode;
  headerContent?: ReactNode;
  footer?: ReactNode;
  hideFooter?: boolean;
  borderless?: boolean;
  modalInlineStyle?: CSSProperties;
  dataComponent?: string;
  dataSection?: string;
};

export const Modal: FC<ModalProps> = ({
  isOpen,
  title,
  onCancel: onClose = () => {},
  onOk: onApply = () => {},
  testID,
  className,
  headerClassName,
  bodyClassName,
  containerClassName,
  footerClassName,
  shouldRenderCloseIconButton = true,
  shouldRenderCancelButton = true,
  shouldCloseOnOverlayClick = true,
  okButtonText = <FormattedMessage id="global.apply" defaultMessage="Apply" />,
  cancelButtonText = (
    <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
  ),
  okButtonProps,
  cancelButtonProps,
  children,
  headerContent,
  footer,
  hideFooter = false,
  borderless = false,
  modalInlineStyle,
  dataComponent,
  dataSection,
}) => {
  const { isMobile } = useMedia();

  const showCloseIcon =
    typeof onClose === 'function' && shouldRenderCloseIconButton;

  // Close modal on ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Portal>
          <div
            className={cn(styles.modal, className)}
            data-modal-root
            tabIndex={-1}
            data-testid={testID}
            data-component={dataComponent}
            data-section={dataSection}
          >
            <motion.div
              className={styles.overlay}
              {...(shouldCloseOnOverlayClick ? { onClick: onClose } : {})}
              key="modal-overlay"
              transition={{ duration: MODAL_ANIMATION_DURATION }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              className={cn(styles.container, containerClassName)}
              style={{ ...modalInlineStyle }}
              key="modal"
              exit="exit"
              variants={
                isMobile
                  ? mobileModalAnimationVariants
                  : defaultModalAnimationVariants
              }
              initial="initial"
              animate={isOpen ? 'open' : 'closed'}
            >
              <header
                className={cn(
                  styles.header,
                  borderless && styles.borderless,
                  headerClassName,
                )}
              >
                {headerContent || (
                  <h3 className={cn(styles.title, isMobile ? 'h2m' : 'h3')}>
                    {title}
                  </h3>
                )}
                {showCloseIcon && (
                  <Icon
                    className={styles.crossButton}
                    name="close"
                    size={isMobile ? 32 : 40}
                    onClick={onClose}
                  />
                )}
              </header>

              <div
                className={cn(
                  styles.modalBody,
                  hideFooter && styles.hideFooter,
                  bodyClassName,
                )}
              >
                {children}
              </div>

              {hideFooter ? null : (
                <footer
                  className={cn(
                    styles.footer,
                    borderless && styles.borderless,
                    footerClassName,
                  )}
                >
                  {footer || (
                    <>
                      {shouldRenderCancelButton && (
                        <Button
                          variant="secondary"
                          onClick={onClose}
                          {...cancelButtonProps}
                        >
                          {cancelButtonText}
                        </Button>
                      )}

                      <Button onClick={onApply} {...okButtonProps}>
                        {okButtonText}
                      </Button>
                    </>
                  )}
                </footer>
              )}
            </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
};
